import { useCallback, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import pop from "../ui/popovers.module.css";
import { useAnchor } from "../ui/use-anchor";
import { useDismiss } from "../ui/use-dismiss";
import cat from "./catalogue.module.css";

export interface StatusOption {
  value: string;
  label: string;
  /** How many rows it holds, shown beside it. */
  count: number;
}

/**
 * **A status filter as toggles, several on at once** (Emile, 2026-10-17: "a
 * toggle and not a one select list"). A select-shaped button opens them; each
 * says how many rows it holds, and none on means every status. The catalogue
 * and Inventory's shelf both use it.
 */
export function StatusToggles({
  options,
  on,
  onToggle,
  onClear,
  testId,
}: {
  options: readonly StatusOption[];
  on: ReadonlySet<string>;
  onToggle: (value: string) => void;
  onClear: () => void;
  /** The button's test id; each switch is `${testId}-${value}`. */
  testId: string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLSpanElement>(null);
  const panelId = useId();
  const close = useCallback((how: "outside" | "escape") => {
    setOpen(false);
    if (how === "escape") button.current?.focus();
  }, []);
  useDismiss(open, close, wrap, panel);
  const place = useAnchor(open, button, "start");

  const label =
    on.size === 0
      ? "All statuses"
      : on.size === 1
        ? (options.find((x) => on.has(x.value))?.label ?? "Status")
        : `${String(on.size)} statuses`;

  return (
    <span className={cat.toggleWrap} ref={wrap}>
      <button
        ref={button}
        type="button"
        className={`${cat.select} ${cat.selectSmall} ${cat.toggleButton}`}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`Filter by status: ${label}`}
        onClick={() => setOpen((was) => !was)}
        data-testid={testId}
      >
        <span className={cat.toggleLabel}>{label}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {open &&
        createPortal(
          <span id={panelId} ref={panel} className={pop.menu} style={place}>
            {options.map(({ value, label: name, count }) => (
              <button
                key={value}
                type="button"
                role="switch"
                aria-checked={on.has(value)}
                className={`${pop.item} ${cat.toggleItem}`}
                onClick={() => onToggle(value)}
                data-testid={`${testId}-${value}`}
              >
                <span className={on.has(value) ? `${cat.switch} ${cat.switchOn}` : cat.switch} />
                <span>{name}</span>
                <span className={cat.muted}>{count}</span>
              </button>
            ))}
            {on.size > 0 && (
              <>
                <span className={pop.rule} aria-hidden="true" />
                <button
                  type="button"
                  className={pop.item}
                  onClick={onClear}
                  data-testid={`${testId}-clear`}
                >
                  Show all statuses
                </button>
              </>
            )}
          </span>,
          document.body,
        )}
    </span>
  );
}

/** Toggle one value in a set, as a new set. */
export function toggled<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}
